import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import { useGetAccessoryQuery } from "../../hooks-saleor/getAccessoryQuery"
import LoadingSpinner from "../atoms/LoadingSpinner"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { Link, useLocation } from "react-router-dom"
import { CartStructure, Product } from "../interfaces/SaleorTypes"
import { useGetTerminalQuery } from "../../hooks-saleor/getTerminalQuery"
import styled from "styled-components"
import { ContactPhoneIcon } from "../atoms/icons/ContactPhoneIcon"
import NavigationTextButton from "../atoms/buttons/NavigationTextButton"
import H3 from "../atoms/typography/H3"
import H1 from "../atoms/typography/H1"
import AccessoriesCard from "../organisms/store/AccessoriesCard"
import H2 from "../atoms/typography/H2"
import { theme } from "../theme"
import ProductCard from "../organisms/store/ProductCards/TerminalProductCard"
import InstantProductCard from "../organisms/store/ProductCards/InstantProductCard"
import { MediaQuery } from "../../utils/design-helpers"
import { useInView, useScrollToRef } from "../../utils/helpers"
import { captureExceptionWithContext } from "../../utils/sentry"
import { useStoreHomePage } from "../../hooks/store/useStoreHomePage"

interface StorePageProps {
  addToCart: (products: CartStructure[]) => void
}

interface PromoBannerImgProps {
  left: string
  right: string
}

const IconContainer = styled.div`
  cursor: pointer;

  :hover {
    filter: brightness(60%);
  }
`

const MobileNav = styled.div`
  display: flex;
  width: 100%;
  overflow-x: scroll;
  margin-bottom: 2rem;
  justify-content: space-between;
  max-width: 100vw;
  position: sticky;
  top: 54px;
  left: 0;
  z-index: 3;
  padding: 1rem;
  background: rgba(250, 250, 250, 0.8);
  backdrop-filter: blur(3px);
`

const PromoBannerImg = styled.img<PromoBannerImgProps>`
  width: 100%;
  height: 573px;
  object-fit: cover;
  object-position: ${(props) => props.left} ${(props) => props.right};
`

const PromoTextContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`

const PromoText = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 3rem 1rem;
`

const NavigationSection = styled.div`
  position: sticky;
  top: 72px;
  left: 0;
  z-index: 3;
  background: rgba(250, 250, 250, 0.8);
  backdrop-filter: blur(3px);
  padding-top: 1.38rem;
  padding-bottom: 1.38rem;

  ${MediaQuery.XS} {
    top: 10px;
    left: 24px;
    position: absolute;
  }
`
const NAVBAR_OFFSET = 160 // px

const StorePage: React.FunctionComponent<StorePageProps> = ({ addToCart, ...props }) => {
  const { accessories, loading: loadingA, error: errorA } = useGetAccessoryQuery()
  const { terminals, loading: loadingT, error: errorT } = useGetTerminalQuery()
  const { storeHomePage, shpError, shpLoading } = useStoreHomePage()
  const [selectedHeader, setSelectedHeader] = React.useState<Number>(1)

  const location = useLocation()

  const terminalRef = React.useRef<HTMLDivElement>(null)
  const accessoryRef = React.useRef<HTMLDivElement>(null)
  const instantRef = React.useRef<HTMLDivElement>(null)
  const kuRef = React.useRef<HTMLDivElement>(null)
  const secureRef = React.useRef<HTMLDivElement>(null)

  const terminalRefIsInView = useInView(terminalRef)
  const accessoryRefIsInView = useInView(accessoryRef)
  const instantRefIsInView = useInView(instantRef)

  const scrollToRef = useScrollToRef(NAVBAR_OFFSET)

  const refs: { [key: string]: React.RefObject<HTMLDivElement> } = {
    terminalSection: terminalRef,
    accessorySection: accessoryRef,
    instantSection: instantRef,
    ku: kuRef,
    secure: secureRef,
  }

  React.useEffect(() => {
    if (terminalRefIsInView && !accessoryRefIsInView && !instantRefIsInView) {
      setSelectedHeader(1)
    } else if (!terminalRefIsInView && !accessoryRefIsInView && instantRefIsInView) {
      setSelectedHeader(2)
    } else if (!terminalRefIsInView && accessoryRefIsInView && !instantRefIsInView) {
      setSelectedHeader(3)
    }
  }, [terminalRefIsInView, accessoryRefIsInView, instantRefIsInView])

  if (errorA) {
    captureExceptionWithContext(errorA, "error fetching accessories from saleor")
  }
  if (errorT) {
    captureExceptionWithContext(errorT, "error fetching terminals from saleor")
  }
  if (shpError) {
    captureExceptionWithContext(shpError, "error fetching store home page from Contentful")
  }

  React.useEffect(() => {
    document.title = "Store"
  }, [])

  const scrollToTerminals = () => {
    scrollToRef(terminalRef)
  }
  const scrollToRental = () => {
    scrollToRef(instantRef)
  }
  const scrollToAccessories = () => {
    scrollToRef(accessoryRef)
  }

  React.useEffect(() => {
    if (
      terminalRef.current &&
      accessoryRef.current &&
      instantRef.current &&
      !loadingA &&
      !loadingT &&
      !shpLoading &&
      location.hash
    ) {
      const ref = refs[location.hash.slice(1)]
      if (ref && ref.current) scrollToRef(ref)
    }
  }, [location, terminalRef, accessoryRef, instantRef, loadingA, loadingT, shpLoading])

  return (
    <div>
      <div style={{ position: "relative" }}>
        <PromoBannerImg
          src={storeHomePage?.coverImage?.url}
          alt={storeHomePage?.coverImage?.title}
          left={storeHomePage?.coverImageLeft ? `${storeHomePage?.coverImageLeft}%` : "center"}
          right={storeHomePage?.coverImageTop ? `${storeHomePage?.coverImageTop}%` : "center"}
        />
        <PromoTextContainer>
          <Container style={{ height: "100%" }}>
            <PromoText>
              <H1 color="white">{storeHomePage?.heading}</H1>
              <H2 color="white">{storeHomePage?.subtitle}</H2>
            </PromoText>
          </Container>
        </PromoTextContainer>
      </div>

      <Container id="beginning" style={{ position: "relative" }}>
        <NavigationSection>
          <Row align="center" justify="between" nogutter style={{ paddingLeft: 15, paddingRight: 30 }}>
            <Hidden xs>
              <Col lg={6} md={12} xs={12}>
                <Row>
                  <Col>
                    <NavigationTextButton title="Terminals" active={selectedHeader == 1} onClick={scrollToTerminals} />
                  </Col>
                  <Col>
                    <NavigationTextButton title="Rental" active={selectedHeader == 2} onClick={scrollToRental} />
                  </Col>
                  <Col>
                    <NavigationTextButton
                      title="Accessories"
                      active={selectedHeader == 3}
                      onClick={scrollToAccessories}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={2} xs={0} />
            </Hidden>
            <Col lg={4} md={6} xs={12}>
              <Visible md>
                <HorizontalSpacer spacing={2} />
              </Visible>
              <Row justify="end">
                <Visible xs>
                  <Spacer spacing={1} />
                </Visible>
                <Link to="/contact">
                  <Hidden xs>
                    <IconContainer>
                      <Col>
                        <Row align="center" justify="end">
                          <ContactPhoneIcon color={theme.colors.dark} />
                          <Spacer spacing={1} />
                          <div>
                            <H3 color={theme.colors.dark}>Contact sales</H3>
                          </div>
                        </Row>
                      </Col>
                    </IconContainer>
                  </Hidden>
                </Link>
              </Row>
            </Col>
          </Row>
        </NavigationSection>

        <HorizontalSpacer spacing={4} />

        <Visible xs md>
          <MobileNav>
            <NavigationTextButton title="Terminals" active={selectedHeader == 1} onClick={scrollToTerminals} />
            <Spacer spacing={2} />
            <NavigationTextButton title="Accessories" active={selectedHeader == 2} onClick={scrollToAccessories} />
            <Spacer spacing={2} />

            <NavigationTextButton title="Rental" active={selectedHeader == 3} onClick={scrollToRental} />
          </MobileNav>
        </Visible>
        <Visible xs>
          <Link to="/contact">
            <Row align="center" nogutter justify="end">
              <ContactPhoneIcon color={theme.colors.dark} />
              <Spacer spacing={1} />
              <div>
                <H3 color={theme.colors.dark}>Contact sales</H3>
              </div>
              <Spacer spacing={1} />
            </Row>
          </Link>
          <HorizontalSpacer spacing={3} />
        </Visible>

        {loadingA && loadingT ? (
          <LoadingSpinner />
        ) : (
          <div>
            <HorizontalSpacer spacing={0} ref={terminalRef} id="terminalSection" />
            {terminals &&
              terminals.map((product: Product, index: number) => (
                <div key={index} id={index === 0 ? "ku" : "secure"} ref={index === 0 ? kuRef : secureRef}>
                  <ProductCard product={product} addToCart={addToCart} />
                </div>
              ))}
            <div ref={instantRef} id="instantSection" />

            <HorizontalSpacer spacing={1} />

            <HorizontalSpacer spacing={1} />
            <InstantProductCard />

            <HorizontalSpacer spacing={3} />

            <HorizontalSpacer spacing={3} />
            <div ref={accessoryRef} id="accessories" />

            {accessories &&
              accessories.map((product: Product, index: number) => (
                <div key={index} id={`accessory${index}`}>
                  <AccessoriesCard product={product} addToCart={addToCart} hideAddToCart={false} />
                  <HorizontalSpacer spacing={2} />
                </div>
              ))}

            <HorizontalSpacer spacing={2} />
          </div>
        )}
      </Container>

      <HorizontalSpacer spacing={2} />
    </div>
  )
}

export default StorePage
